import React from 'react';
import type { ProratedRentErrors, TenantStatus } from './types';

// Constellation
import {
  AdornedInput,
  Adornment,
  ButtonGroup,
  DatePickerInput,
  FormActions,
  FormField,
  FormHelp,
  IconCalendarOutline,
  Input,
  Label,
  Select,
  Text,
  Form as ZCForm,
} from '@zillow/constellation';

// Misc / Utils
import * as S from './styles';

interface FormProps {
  tenantStatuses: Array<TenantStatus>;
  setTenantStatus: (status: TenantStatus) => void;
  setMonthlyRent: (rent: string) => void;
  setMoveDate: (value: string) => void;
  handleCalculateButtonClick: () => void;
  errors: ProratedRentErrors;
}

// To workaround a @zillow-types/constellation issue, where children is required in FormField
const Empty: React.FunctionComponent<React.PropsWithChildren<React.PropsWithChildren<unknown>>> = () => <></>;

const Form: React.FunctionComponent<React.PropsWithChildren<React.PropsWithChildren<FormProps>>> = ({
  tenantStatuses,
  setTenantStatus,
  setMonthlyRent,
  setMoveDate,
  handleCalculateButtonClick,
  errors,
}: FormProps) => {
  const handleChangeTenantStatus = (e: React.ChangeEvent<HTMLSelectElement>) =>
    setTenantStatus(e.target.value as TenantStatus);
  // Necessary because the type of DatePickerInputProps['onChange'] resolves to:
  // ((event: React.ChangeEvent<HTMLInputElement) => void) & ((value: string) => void)
  // AKA Function Overload: https://www.typescriptlang.org/docs/handbook/functions.html#overloads
  const handleChangeMoveDate = (e: string | React.ChangeEvent<HTMLInputElement>) => {
    if (typeof e === 'object') {
      setMoveDate(e.target.value);
    } else if (typeof e === 'string') {
      setMoveDate(e);
    }
  };
  const handleChangeRent = (e: React.ChangeEvent<HTMLInputElement>) => setMonthlyRent(e.target.value);
  return (
    <ZCForm>
      <FormField
        label={<Label>Select tenant status</Label>}
        control={
          <Select onChange={handleChangeTenantStatus}>
            {tenantStatuses.map((status: TenantStatus) => (
              <option key={status}>{status}</option>
            ))}
          </Select>
        }
      >
        <Empty />
      </FormField>
      <FormField
        label={<Label>Enter monthly rent</Label>}
        control={
          <AdornedInput
            input={<Input placeholder="Enter monthly rent amount" onChange={handleChangeRent} />}
            leftAdornment={
              <Adornment>
                <Text>$</Text>
              </Adornment>
            }
          />
        }
        error={!!errors.monthlyRent}
        description={errors.monthlyRent && <FormHelp>{errors.monthlyRent}</FormHelp>}
      >
        <Empty />
      </FormField>
      <FormField
        label={<Label>Enter tenant move date</Label>}
        control={
          <AdornedInput
            input={<DatePickerInput placeholder="Select date" onChange={handleChangeMoveDate} />}
            rightAdornment={
              <Adornment>
                <IconCalendarOutline fontColor="brand" />
              </Adornment>
            }
          />
        }
        error={!!errors.moveDate}
        description={errors.moveDate && <FormHelp>{errors.moveDate}</FormHelp>}
      >
        <Empty />
      </FormField>
      <FormActions as={ButtonGroup} aria-label="Calculate">
        <S.CenterButton
          buttonType="primary"
          onClick={(e: React.MouseEvent) => {
            e.preventDefault();
            handleCalculateButtonClick();
          }}
          fluid={true}
        >
          Calculate
        </S.CenterButton>
      </FormActions>
    </ZCForm>
  );
};

export default Form;
